import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/site/Header';
import Hero from './components/site/Hero';
import TrustedBy from './components/site/TrustedBy';
import ExperienceGrid from './components/site/ExperienceGrid';
import WhoWeWorkWith from './components/site/WhoWeWorkWith';
import HowItWorks from './components/site/HowItWorks';
import CaseStudies, { useWorkItems } from './components/site/CaseStudies';
import { QuoteBand } from './components/site/PageParts';
import MarqueeCTA from './components/site/MarqueeCTA';
import BlogPosts from './components/site/BlogPosts';
import LiveEventsTeaser from './components/site/LiveEventsTeaser';
import Footer from './components/site/Footer';
import ArtistMerch from './pages/ArtistMerch';
import Production from './pages/Production';
import TechniquePage from './pages/TechniquePage';
import AudiencePage from './pages/AudiencePage';
import Work from './pages/Work';
import Pricing from './pages/Pricing';
import LiveEvents from './pages/LiveEvents';
import BlogIndex from './pages/BlogIndex';
import BlogPost from './pages/BlogPost';
import ThankYou from './pages/ThankYou';
import NotFound from './pages/NotFound';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import ProtectedRoute from './components/admin/ProtectedRoute';
import QuoteModal from './components/site/QuoteModal';
import { QuoteModalProvider } from './contexts/QuoteModalContext';
import { AuthProvider } from './contexts/AuthContext';
import { LEGACY_HOME_HASHES } from './siteNav';
import useSeo from './hooks/useSeo';
import './App.css';

function ScrollToHashOrTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname, hash]);
  return null;
}

// /artist-development (+ #full-production) → new pages; hashes never reach a server, so this runs in the browser.
function ArtistDevelopmentRedirect() {
  const { hash } = useLocation();
  return <Navigate to={hash === '#full-production' ? '/production' : '/artist-merch'} replace />;
}

function Home() {
  const { hash } = useLocation();
  const work = useWorkItems();
  const recent = [
    ...work.filter((p) => p.kind === 'production').slice(0, 3),
    ...work.filter((p) => p.kind === 'live-printing').slice(0, 3),
  ];
  useSeo({
    title: 'Custom Merch Production & Manufacturing | Pusha Teez',
    description:
      'Merch production for artists, bands and brands: screen printing, embroidery, DTG & DTF, cut and sew and fulfillment from LA / OC. Get a quote.',
    path: '/',
  });
  if (LEGACY_HOME_HASHES[hash]) return <Navigate to={LEGACY_HOME_HASHES[hash]} replace />;
  return (
    <>
      <Header />
      <main>
        <Hero />
        <TrustedBy />
        <ExperienceGrid
          title="What we"
          accent="produce"
          intro="In-house production at our LA / OC facilities — low minimums, a typical turnaround of 7–10 days and nationwide shipping. Pick a technique to learn more."
        />
        <WhoWeWorkWith />
        <HowItWorks />
        <MarqueeCTA />
        <CaseStudies items={recent} title="Recent" accent="Work" moreLink={{ to: '/work', label: 'See all work' }} />
        <LiveEventsTeaser />
        <BlogPosts limit={3} />
        <QuoteBand title="Ready to make it?" />
      </main>
      <Footer />
    </>
  );
}

function PublicShell({ children }) {
  return (
    <QuoteModalProvider>
      <ScrollToHashOrTop />
      {children}
      <QuoteModal />
    </QuoteModalProvider>
  );
}

function App() {
  return (
    <div className="App bg-black min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/*"
              element={
                <PublicShell>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/production" element={<Production />} />
                    <Route path="/production/:technique" element={<TechniquePage />} />
                    <Route path="/who-we-serve/:audience" element={<AudiencePage />} />
                    <Route path="/artist-merch" element={<ArtistMerch />} />
                    <Route path="/artist-development" element={<ArtistDevelopmentRedirect />} />
                    <Route path="/work" element={<Work />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/live-events" element={<LiveEvents />} />
                    <Route path="/blog" element={<BlogIndex />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/thank-you" element={<ThankYou />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </PublicShell>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
